<template>
  <div class="objective-modal">
    <div ref="objectiveEditModal" class="modal fade" data-bs-backdrop="static" id="objectiveEditModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" v-draggable-modal>
        <div class="modal-content bg-primary">
          <div class="modal-header">
            <div class="h5 modal-title text-light me-2"> {{ modalTitle }} </div>
            <DocsInfoLink color="light" target="viewObjective"></DocsInfoLink>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body bg-light">
            <div v-if="elementType === 'objective'" class="form-link">
              <div class="mb-3">
                <label for="objectiveOrgUnit" class="form-label">{{ $t("views.objective.edit.orgUnit") }}<span class="text-danger">*</span></label>
                <select v-model="form.objective.orgUnit.content" id="objectiveOrgUnit" :class="form.objective.orgUnit.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option v-for="orgUnit in orgUnitsList" :key="orgUnit.id" :value="orgUnit.id">{{ orgUnit.name }}</option>
                </select>
                <div class="invalid-feedback">{{ form.objective.orgUnit.error }}</div>
              </div>
              <div class="mb-3">
                <label for="objectiveParentId" class="form-label">{{ $t("views.objective.edit.parentObjective") }}<span class="text-danger">*</span></label>
                <select v-model="form.objective.parentId.content" id="objectiveParentId" class="form-select">
                  <option value="">{{ $t("views.objective.edit.noParentObjective") }}</option>
                  <option v-for="objective in objectivesList" :key="objective.id" :value="objective.id" :disabled="((mode === 'update' && objective.id === selectedObjective.id) || objective.org) ? true : false">
                    {{ objective.category === 1 ? objective.name.toUpperCase() : objective.name }}
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label for="objectiveResponsible" class="form-label">{{ $t("views.objective.edit.responsible") }}</label>
                <select v-model="form.objective.responsible.content" id="objectiveResponsible" :class="form.objective.responsible.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option v-for="responsible in employees" :key="responsible.id" :value="responsible.id">
                    {{ responsible.lastName }}, {{ responsible.firstName }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ form.objective.responsible.error }}</div>
              </div>

              <div class="mb-3">
                <label for="objectiveName" class="form-label">{{ $t("views.objective.edit.name") }}<span class="text-danger">*</span></label>
                <input v-model="form.objective.name.content" id="objectiveName" :class="form.objective.name.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.objective.name.error }}</div>
              </div>

              <div class="mb-3">
                <label for="objectiveDescription" class="form-label">{{ $t("views.objective.edit.description") }}</label>
                <RTEditor ref="objectiveDescription" v-model="form.objective.description.content" contentType="html" id="objectiveDescription"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.objective.description.error }}</div>
              </div>

              <div class="row mb-3">
                <label for="role" class="form-label">{{ $t("views.objective.edit.statusLabel") }}<span class="text-danger">*</span></label>
                <select v-model="form.objective.status.content" id="role" class="form-select">
                  <option value="C">{{ $t("views.objective.edit.status.C") }}</option>
                  <option value="A">{{ $t("views.objective.edit.status.A") }}</option>
                  <option value="F">{{ $t("views.objective.edit.status.F") }}</option>
                </select>
              </div>

              <!-- Radio Buttons Time Interval -->
              <div class="row">
                <label class="form-label">{{ $t("views.objective.edit.timeInterval") }}<span class="text-danger">*</span></label>
              </div>

              <div class="mb-3">
                <!-- multi-year breakthrough -->
                <div class="form-check form-check-inline">
                  <input  class="form-check-input" type="radio" name="objectiveCategory" id="objectiveCategoryBreakthrough"  :value="1" v-model.number="form.objective.category.content" :disabled="!categoryEnabled[1]" >
                  <label class="form-check-label ms-1" for="objectiveCategoryBreakthrough"> {{ $t("views.objective.edit.breakthrough") }} </label>
                </div>

                <!-- yearly objective -->
                <div class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" name="objectiveCategory" id="objectiveCategoryYear" :value="0" v-model.number="form.objective.category.content" :disabled="!categoryEnabled[0]">
                  <label class="form-check-label ms-1" for="objectiveCategoryYear"> {{ $t("views.objective.edit.yearly") }} </label>
                </div>

                <!-- quarterly objective -->
                <div class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" name="objectiveCategory" id="objectiveCategoryQuarter" :value="2" v-model.number="form.objective.category.content" :disabled="!categoryEnabled[2]">
                  <label class="form-check-label ms-1" for="objectiveCategoryQuarter"> {{ $t("views.objective.edit.quarterly") }} </label>
                </div>
              </div>

              <!-- Select Time Interval, validFrom, validTo -->
              <div class="row mb-3">

                <!-- Breakthrough: Startjahr -->
                <div class="col-3" v-if="form.objective.category.content === 1">
                  <label class="form-label"> {{ $t("views.objective.edit.yearFrom") }} <span class="text-danger">*</span> </label>
                  <select class="form-select" v-model.number="breakthroughStartYear">
                    <option v-for="y in nextYears" :key="y" :value="y">{{ y }}</option>
                  </select>
                </div>

                <!-- Breakthrough: Endjahr -->
                <div class="col-3" v-if="form.objective.category.content === 1">
                  <label class="form-label"> {{ $t("views.objective.edit.yearTo") }} <span class="text-danger">*</span> </label>
                  <select class="form-select" v-model.number="breakthroughEndYear">
                    <option v-for="y in nextYears" :key="y" :value="y">{{ y }}</option>
                  </select>
                </div>

                <!-- Jahresziel -->
                <div class="col-3" v-if="form.objective.category.content === 0">
                  <label class="form-label"> {{ $t("views.objective.edit.year") }} <span class="text-danger">*</span> </label>
                  <select class="form-select" v-model.number="selectedYear">
                    <option v-for="y in nextYears" :key="y" :value="y">{{ y }}</option>
                  </select>
                </div>

                <!-- Quartalsziel -->
                <div class="col-3" v-if="form.objective.category.content === 2">
                  <label class="form-label"> {{ $t("views.objective.edit.quarter") }} <span class="text-danger">*</span> </label>
                  <select class="form-select" v-model="selectedQuarter">
                    <option v-for="q in nextQuarters" :key="q.value" :value="q.value"> {{ q.label }} </option>
                  </select>
                </div>

                <!-- Startdatum -->
                <div class="col-3">
                  <label for="objectiveValidFrom" class="form-label"> {{ $t("views.objective.edit.validFrom") }} </label>
                  <input v-model="form.objective.validFrom.content" type="date" id="objectiveValidFrom" class="form-control" :disabled="true" >
                </div>

                <!-- Enddatum -->
                <div class="col-3">
                  <label for="objectiveValidTo" class="form-label"> {{ $t("views.objective.edit.validTo") }} </label>
                  <input v-model="form.objective.validTo.content" type="date"  id="objectiveValidTo" class="form-control" :disabled="true" >
                </div>

              </div>

              <!-- Auswahlbuttons KPI / Key Results -->
              <div v-if="selectedObjective.criterion == null" class="row">
                <div class="col-12">
                  <div class="mb-2">{{ $t("views.objective.edit.performanceBenchmarks") }}</div>
                  <div class="btn-group btn-group-objectives-view" role="group">
                    <button @click="toggleCriterion('KRT')" type="button" class="btn btn-sm btn-outline-primary" :class="criterion === 'KRT' ? 'active' : ''">{{ $t("views.objective.edit.btnKeyResults") }}</button>
                    <span class="mx-2 mt-1">- {{ $t("general.or") }} -</span>
                    <button @click="toggleCriterion('KPI')" type="button" class="btn btn-sm btn-outline-primary" :class="criterion === 'KPI' ? 'active' : ''">{{ $t("views.objective.edit.btnKPIs") }}</button>
                  </div>
                </div>
              </div>

              <!-- Auswahl KPI -->
              <div v-if="selectedObjective.criterion === 'KPI'">
                <div class="row">
                  <div class="col-4">
                    <label for="kpis" class="form-label">
                      {{ $t("views.objective.edit.kpis") }}
                    </label>
                    <div class="input-group">
                      <select @change="addKPI()" v-model="kpiSelected" id="kpis" class="form-select">
                        <option :value="{ id: 0, name: 'Auswählen...' }">{{ kpiSelected.name }}</option>
                        <option v-for="kpi in kpisList" :key="kpi.id" :value="kpi" :disabled="kpi.folder ? true : false">{{ kpi.name }}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="row selected-kpis">
                  <div class="col-12 mt-3">
                    <div v-for="selectedKPI, index in form.objective.kpisEdit.content" :key="'selected-kpi' + selectedKPI.id" class="input-group input-group-sm mb-3">
                      <span class="input-group-text">
                        {{ selectedKPI.name }}
                      </span>
                      <button @click="deleteKPI(index)" class="btn btn-sm btn-outline-danger" type="button">
                        <svg class="bi" width="18" height="18" fill="currentColor">
                          <use xlink:href="@/assets/icons/icons.svg#trash-fill"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Auswahl Key Results -->
              <div v-if="selectedObjective.criterion === 'KRT'">
                <div class="card">
                  <div class="card-header">{{ $t("views.objective.edit.btnKeyResults") }}</div>
                  <div class="card-body pt-0">
                    <div>
                      <button @click="addKeyResult()" type="button" class="btn btn-sm btn-outline-primary mt-3">+ {{ $t("views.objective.edit.btnAddKeyResult") }}</button>
                    </div>
                    <div v-for="keyresult, index in form.objective.keyresultsEdit.content" :key="'keyresult' + keyresult.id" class="row mt-3">
                      <div class="col-4">
                        <label for="name" class="form-label">
                          {{ $t("views.objective.edit.keyResultName") }}<span class="text-danger">*</span>
                        </label>
                        <input v-model="keyresult.name" id="keyresultName" class="form-control">
                      </div>
                      <div class="col-3">
                        <label for="name" class="form-label">
                          {{ $t("views.objective.keyresult.unit") }}<span class="text-danger">*</span>
                        </label>
                        <input v-model="keyresult.unit" id="keyresultUnit" class="form-control">
                      </div>
                      <div class="col-2">
                        <label for="name" class="form-label">
                          {{ $t("views.objective.keyresult.jop") }}<span class="text-danger">*</span>
                        </label>
                        <input v-model="keyresult.jop" type="number" min="0" id="keyresultJOP" class="form-control">
                      </div>
                      <div class="col-2">
                        <label for="name" class="form-label">
                          {{ $t("views.objective.edit.keyResultObjective") }}<span class="text-danger">*</span>
                        </label>
                        <input v-model="keyresult.obj" type="number" id="keyresultObjective" class="form-control">
                      </div>
                      <div class="col-1 text-end mt-auto">
                        <button @click="deleteKeyResult(index)" class="btn btn-outline-danger px-2" type="button">
                          <svg class="bi" width="16" height="16" fill="currentColor">
                            <use xlink:href="@/assets/icons/icons.svg#trash-fill"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="elementType === 'record'" class="form-link">
              <div class="h4 mb-3">{{ getKeyResultName() }}</div>
              <div class="mb-3">
                <label for="recordDate" class="form-label">{{ $t("views.objective.keyresult.date") }}<span class="text-danger">*</span></label>
                <input v-model="form.record.date.content" type="date" id="recordDate" :class="form.record.date.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.record.date.error }}</div>
              </div>
              <div class="mb-3">
                <label for="recordActual" class="form-label">{{ $t("views.objective.keyresult.actual") }}<span class="text-danger">*</span></label>
                <input v-model="form.record.actual.content" type="number" id="recordActual" :class="form.record.actual.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.record.actual.error }}</div>
              </div>
              <div class="mb-3">
                <label for="recordForecast" class="form-label">{{ $t("views.objective.keyresult.forecast") }}<span class="text-danger">*</span></label>
                <input v-model="form.record.forecast.content" type="number" id="recordForecast" :class="form.record.forecast.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.record.forecast.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewComment" class="form-label">{{ $t("views.objective.keyresult.comment") }}</label>
                <RTEditor ref="reviewComment" v-model="form.record.comment.content" contentType="html" id="reviewComment"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.record.comment.error }}</div>
              </div>
            </div>
            <div v-if="elementType === 'review'" class="form-link">
              <div class="h4 mb-3">{{ selectedObjective.name }}</div>
              <div class="mb-3">
                <label for="reviewDate" class="form-label">{{ $t("views.objective.detail.reviewDate") }}<span class="text-danger">*</span></label>
                <input v-model="form.review.date.content" type="date" id="reviewDate" :class="form.review.date.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.review.date.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewAssessment" class="form-label">{{ $t("views.objective.detail.reviewAssessment") }}<span class="text-danger">*</span></label>
                <RTEditor ref="reviewAssessment" v-model="form.review.assessment.content" contentType="html" id="reviewAssessment"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.review.assessment.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewConversion" class="form-label">{{ $t("views.objective.detail.reviewConversion") }}<span class="text-danger">*</span></label>
                <select v-model="form.review.conversion.content" id="reviewConversion" :class="form.review.conversion.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option value="0">0%</option>
                  <option value="1">25%</option>
                  <option value="2">50%</option>
                  <option value="3">75%</option>
                  <option value="4">100%</option>
                </select>
                <div class="invalid-feedback">{{ form.review.conversion.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewPrediction" class="form-label">{{ $t("views.objective.detail.reviewPrediction") }}<span class="text-danger">*</span></label>
                <select v-model="form.review.prediction.content" id="reviewPrediction" :class="form.review.prediction.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option value="Y">{{ $t("views.objective.detail.review.prediction.y") }}</option>
                  <option value="D">{{ $t("views.objective.detail.review.prediction.d") }}</option>
                  <option value="N">{{ $t("views.objective.detail.review.prediction.n") }}</option>
                </select>
                <div class="invalid-feedback">{{ form.review.prediction.error }}</div>
              </div>
              <div class="mb-3">
                <label for="status" class="form-label">{{ $t("views.objective.edit.statusLabel") }}<span class="text-danger">*</span></label>
                <select v-model="form.review.status.content" id="reviewStatus" class="form-select" :disabled="this.$parent.objectiveModalMode === 'add' ? false : true">
                  <option value="C">{{ $t("views.objective.edit.status.C") }}</option>
                  <option value="A">{{ $t("views.objective.edit.status.A") }}</option>
                  <option value="F">{{ $t("views.objective.edit.status.F") }}</option>
                </select>
              </div>
            </div>
            <div v-if="elementType === 'measure'" class="form-link">
              <div class="mb-3">
                <label for="measureTitle" class="form-label">{{ $t("views.objective.detail.measureTitle") }}<span class="text-danger">*</span></label>
                <input v-model="form.measure.title.content" id="measureTitle" :class="form.measure.title.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.measure.title.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureDescription" class="form-label">{{ $t("views.objective.detail.measureDescription") }}<span class="text-danger">*</span></label>
                <RTEditor ref="measureDescription" v-model="form.measure.measure.content" contentType="html" id="measureDescription"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.measure.measure.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureResponsible" class="form-label">{{ $t("views.objective.detail.measureResponsible") }}<span class="text-danger">*</span></label>
                <select v-model="form.measure.responsible.content" id="measureResponsible" :class="form.measure.responsible.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                    {{ employee.lastName }}, {{ employee.firstName }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ form.measure.responsible.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureConversion" class="form-label">{{ $t("views.objective.detail.measureConversion") }}</label>
                <select v-model="form.measure.conversion.content" id="measureConversion" :class="form.measure.conversion.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option value="0">0%</option>
                  <option value="1">25%</option>
                  <option value="2">50%</option>
                  <option value="3">75%</option>
                  <option value="4">100%</option>
                </select>
                <div class="invalid-feedback">{{ form.measure.conversion.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureValidFrom" class="form-label">{{ $t("views.objective.detail.measureValidFrom") }}<span class="text-danger">*</span></label>
                <input v-model="form.measure.validFrom.content" type="date" id="measureValidFrom" :class="form.measure.validFrom.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.measure.validFrom.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureValidTo" class="form-label">{{ $t("views.objective.detail.measureValidTo") }}<span class="text-danger">*</span></label>
                <input v-model="form.measure.validTo.content" type="date" id="measureValidTo" :class="form.measure.validTo.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.measure.validTo.error }}</div>
              </div>
            </div>
            <div v-if="elementType === 'conflict'" class="form-link">
              <div class="mb-3">
                <label for="conflictDescription" class="form-label">{{ $t("views.objective.detail.conflictDescription") }}<span class="text-danger">*</span></label>
                <RTEditor ref="conflictDescription" v-model="form.conflict.description.content" contentType="html" id="conflictDescription"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.conflict.description.error }}</div>
              </div>
              <div class="mb-3">
                <input v-model="form.conflict.status.content" class="form-check-input" type="checkbox" id="conflictStatus">
                <label for="conflictStatus" class="form-check-label ms-2">{{ $t("views.objective.detail.conflictStatus") }}</label>
              </div>
            </div>
            <div v-if="elementType === 'trial'" class="form-link">
              <div class="mb-3">
                <label for="trialDescription" class="form-label">{{ $t("views.objective.detail.trialDescription") }}<span class="text-danger">*</span></label>
                <input ref="trialDescription" v-model="form.trial.description.content" id="trialDescription" :class="form.trial.description.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.trial.description.error }}</div>
              </div>
              <div class="mb-3">
                <input v-model="form.trial.priority.content" id="trialPriority" class="form-check-input" type="checkbox">
                <label for="trialPriority" class="form-check-label ms-2">{{ $t("views.objective.detail.trialPriority") }}</label>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light justify-content-between">
            <div>
              <button v-if="elementType === 'objective'" type="button" class="btn btn-sm btn-outline-danger" :class="selectedObjective.hasNodes ? 'disabled' : ''" data-bs-dismiss="modal" data-bs-toggle="modal" data-bs-target="#objectiveDeleteModal">Löschen</button>
            </div>
            <div>
              <button type="button" class="btn btn-sm btn-outline-secondary me-2" data-bs-dismiss="modal">{{ $t("general.close") }}</button>
              <button @click="save()" type="button" class="btn btn-sm btn-primary">{{ $t("general.save") }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import initializeForm from '../../composables/initializeForm'
import setFormContent from '../../composables/setFormContent'
import * as validation from '../../composables/validation'
import DocsInfoLink from '../../components/DocsInfoLink.vue'
import RTEditor from '../RTEditor.vue'
import { Modal } from 'bootstrap'
import { ref, nextTick } from 'vue'

export default {
  name: 'ObjectiveEditModal',
  mounted () {
    const myModalDialog = document.getElementById('objectiveEditModal')

    myModalDialog.addEventListener('shown.bs.modal', async () => {
      await nextTick()
      await nextTick() // wichtig bei verschachtelten Komponenten
      const mode = this.$parent.objectiveModalMode
      if (mode === 'add') {
        initializeForm(this.form.objective)
        initializeForm(this.form.record)
        initializeForm(this.form.review)
        initializeForm(this.form.measure)
        initializeForm(this.form.conflict)
        initializeForm(this.form.trial)
        const today = new Date().toISOString().substring(0, 10)
        this.form.record.date.content = today
        this.form.review.date.content = today
        this.form.review.status.content = this.$store.state.selectedObjective.status
      }
      if (mode === 'update') {
        this.form.objective.kpisEdit = structuredClone(this.form.objective.kpis)
        this.form.objective.keyresultsEdit = structuredClone(this.form.objective.keyresults)
      }
    })
    myModalDialog.addEventListener('hidden.bs.modal', event => {
      const mode = this.$parent.objectiveModalMode
      if (mode !== 'add' && mode !== 'delete') {
        if (this.elementType !== 'record') setFormContent(this.form, this.$store.state.selectedObjective, 'objective', this.elementType, this.$parent.objectiveModalElementId)
      }
    })
  },
  data () {
    return {
      kpiSelected: { id: 0, name: 'Auswählen...' },
      form: {
        objective: {
          orgUnit: {
            content: '',
            error: '',
            validate: ['empty']
          },
          parentId: {
            content: '',
            error: '',
            validate: []
          },
          responsible: {
            content: '',
            error: '',
            validate: []
          },
          name: {
            content: '',
            error: '',
            maxLength: 255,
            validate: ['empty', 'length']
          },
          category: {
            content: '',
            error: '',
            validate: []
          },
          description: {
            content: '',
            error: '',
            validate: [],
            rtEditor: true,
            ref: 'objectiveDescription'
          },
          validFrom: {
            content: '',
            error: '',
            validate: ['empty', 'validFrom']
          },
          validTo: {
            content: '',
            error: '',
            validate: ['empty', 'validTo']
          },
          status: {
            content: '',
            error: '',
            validate: []
          },
          kpis: {
            content: '',
            error: '',
            validate: []
          },
          kpisEdit: {
            content: '',
            error: '',
            validate: []
          },
          keyresults: {
            content: '',
            error: '',
            validate: []
          },
          keyresultsEdit: {
            content: '',
            error: '',
            validate: []
          }
        },
        record: {
          date: {
            content: '',
            error: '',
            validate: ['empty']
          },
          actual: {
            content: '',
            error: '',
            validate: ['empty']
          },
          forecast: {
            content: '',
            error: '',
            validate: ['empty']
          },
          comment: {
            content: '',
            error: '',
            validate: [],
            rtEditor: true,
            ref: 'reviewComment'
          }
        },
        review: {
          date: {
            content: '',
            error: '',
            validate: ['empty']
          },
          assessment: {
            content: '',
            error: '',
            validate: ['empty'],
            rtEditor: true,
            ref: 'reviewAssessment'
          },
          conversion: {
            content: '',
            error: '',
            validate: ['empty']
          },
          prediction: {
            content: '',
            error: '',
            validate: ['empty']
          },
          status: {
            content: '',
            error: '',
            validate: []
          }
        },
        measure: {
          title: {
            content: '',
            error: '',
            maxLength: 255,
            validate: ['empty', 'length']
          },
          measure: {
            content: '',
            error: '',
            validate: ['empty'],
            rtEditor: true,
            ref: 'measureDescription'
          },
          responsible: {
            content: '',
            error: '',
            validate: ['empty']
          },
          conversion: {
            content: '',
            error: '',
            validate: []
          },
          validFrom: {
            content: '',
            error: '',
            validate: ['empty', 'validFrom']
          },
          validTo: {
            content: '',
            error: '',
            validate: ['empty', 'validTo']
          }
        },
        conflict: {
          description: {
            content: '',
            error: '',
            validate: ['empty'],
            rtEditor: true,
            ref: 'conflictDescription'
          },
          status: {
            content: '',
            error: '',
            validate: []
          }
        },
        trial: {
          description: {
            content: '',
            error: '',
            maxLength: 1000,
            validate: ['empty', 'length']
          },
          priority: {
            content: '',
            error: '',
            validate: []
          }
        }
      },
      selectedYear: null,
      selectedQuarter: null,
      breakthroughStartYear: null,
      breakthroughEndYear: null
    }
  },
  components: {
    DocsInfoLink,
    RTEditor
  },
  computed: {
    sysParms () {
      return this.$store.state.sysParms
    },
    selectedObjective () {
      return this.$store.state.selectedObjective
    },
    kpisList () {
      return this.$store.state.kpisList
    },
    objectivesList () {
      return this.$store.state.objectivesList
    },
    orgUnitsList () {
      return this.$store.state.orgUnitsList
    },
    employees () {
      return this.$store.state.employees
    },
    elementId () {
      return this.$parent.objectiveModalElementId
    },
    elementType () {
      return this.$parent.objectiveModalElementType
    },
    mode () {
      return this.$parent.objectiveModalMode
    },
    modalTitle () {
      const titles = {
        objective: {
          add: this.$t('views.objective.edit.headingAddObjective'),
          update: this.$t('views.objective.edit.headingEditObjective')
        },
        review: {
          add: this.$t('views.objective.edit.headingAddReview'),
          update: this.$t('views.objective.edit.headingEditReview')
        },
        measure: {
          add: this.$t('views.objective.edit.headingAddMeasure'),
          update: this.$t('views.objective.edit.headingEditMeasure')
        },
        conflict: {
          add: this.$t('views.objective.edit.headingAddConflict'),
          update: this.$t('views.objective.edit.headingEditConflict')
        },
        trial: {
          add: this.$t('views.objective.edit.headingAddTrial'),
          update: this.$t('views.objective.edit.headingEditTrial')
        }
      }
      return titles[this.elementType]?.[this.mode] ?? ''
    },
    categoryEnabled () {
      const model = this.sysParms?.objectiveModel || 'Hybrid'
      return {
        1: model === 'Hoshin' || model === 'Hybrid', // Breakthrough
        0: model === 'Hoshin' || model === 'Hybrid', // Yearly
        2: model === 'OKR' || model === 'Hybrid' // Quarterly
      }
    },

    nextYears () {
      const current = new Date().getFullYear()
      const years = []

      // Breakthrough
      if (this.breakthroughStartYear) years.push(this.breakthroughStartYear)
      if (this.breakthroughEndYear) years.push(this.breakthroughEndYear)

      // Yearly
      if (this.selectedYear) years.push(this.selectedYear)

      // Quarterly
      if (this.selectedQuarter) {
        const yearFromQuarter = Number(this.selectedQuarter.split('-Q')[0])
        years.push(yearFromQuarter)
      }

      // Immer die nächsten 5 Jahre ab heute
      for (let i = 0; i < 5; i++) {
        years.push(current + i)
      }

      // Range bestimmen
      const minYear = Math.min(...years)
      const maxYear = Math.max(...years)

      // Finale Liste
      const result = []
      for (let y = minYear; y <= maxYear; y++) {
        result.push(y)
      }

      return result
    },

    nextQuarters () {
      const current = new Date().getFullYear()
      const years = []

      // Falls ein Quartal ausgewählt ist → Jahr extrahieren
      if (this.selectedQuarter) {
        const yearFromQuarter = Number(this.selectedQuarter.split('-Q')[0])
        years.push(yearFromQuarter)
      }

      // Falls Breakthrough geladen ist → Start/Endjahr berücksichtigen
      if (this.breakthroughStartYear) years.push(this.breakthroughStartYear)
      if (this.breakthroughEndYear) years.push(this.breakthroughEndYear)

      // Falls Yearly geladen ist → Jahr berücksichtigen
      if (this.selectedYear) years.push(this.selectedYear)

      // Immer die nächsten 2 Jahre ab heute (wie bisher)
      years.push(current, current + 1)

      // Range bestimmen
      const minYear = Math.min(...years)
      const maxYear = Math.max(...years)

      // Quartale generieren
      const quarters = []
      for (let year = minYear; year <= maxYear; year++) {
        for (let q = 1; q <= 4; q++) {
          quarters.push({
            label: `${year}-Q${q}`,
            value: `${year}-Q${q}`
          })
        }
      }

      return quarters
    }

  },
  methods: {
    addKPI () {
      if (this.form.objective.kpisEdit.content === '') {
        this.form.objective.kpisEdit.content = []
      }
      if (!this.form.objective.kpisEdit.content.find(kpi => kpi.id === this.kpiSelected.id)) {
        this.kpiSelected.new = true
        this.form.objective.kpisEdit.content.push(this.kpiSelected)
      }
      this.kpiSelected = { id: 0, name: 'Auswählen...' }
    },
    deleteKPI (index) {
      this.form.objective.kpisEdit.content.splice(index, 1)
    },
    getKeyResultName () {
      if (this.selectedObjective.keyresults) {
        return this.selectedObjective.keyresults.filter(keyresult => {
          return keyresult.id === this.$parent.objectiveModalKeyResultId
        })[0].name
      } else { return this.selectedObjective.name }
    },
    addKeyResult () {
      if (this.form.objective.keyresultsEdit.content === '') {
        this.form.objective.keyresultsEdit.content = []
      }
      this.form.objective.keyresultsEdit.content.push({
        name: '',
        unit: '',
        jop: '',
        obj: '',
        records: [],
        selectedRecord: 0,
        new: true
      })
    },
    deleteKeyResult (index) {
      this.form.objective.keyresultsEdit.content.splice(index, 1)
    },
    async save () {
      this.$store.commit('SET_LOADING_STATUS', true)

      let valid = 0
      if (this.elementType === 'objective') valid = validation.fieldValidation(this.form.objective)
      if (this.elementType === 'record') valid = validation.fieldValidation(this.form.record)
      if (this.elementType === 'review') valid = validation.fieldValidation(this.form.review)
      if (this.elementType === 'measure') valid = validation.fieldValidation(this.form.measure)
      if (this.elementType === 'conflict') valid = validation.fieldValidation(this.form.conflict)
      if (this.elementType === 'trial') valid = validation.fieldValidation(this.form.trial)

      if (valid === 0) {
        this.form.objective.kpis = structuredClone(this.form.objective.kpisEdit)
        this.form.objective.keyresults = structuredClone(this.form.objective.keyresultsEdit)

        Object.keys(this.form[this.elementType]).forEach(field => {
          if (this.form[this.elementType][field].rtEditor) {
            const ref = this.form[this.elementType][field].ref
            if (this.$refs[ref]) {
              this.form[this.elementType][field].content = this.$refs[ref].getContent()
            }
          }
        })

        const elementData = Object.assign(
          ...Object.entries(this.form[this.elementType]).map(([key, value]) => ({ [key]: value.content }))
        )
        elementData.id = this.elementId
        let dispatchFunc = ''
        if (this.elementType !== 'objective') {
          if (this.elementType === 'record') {
            elementData.elementType = 'keyresults/' + this.$parent.objectiveModalKeyResultId + '/records'
          } else {
            elementData.elementType = (this.elementType + 's')
          }
          elementData.objectiveId = this.selectedObjective.id
        }
        dispatchFunc = this.elementType === 'objective' ? (this.mode + 'Objective') : (this.mode + 'ObjectiveElement')
        await this.$store.dispatch(dispatchFunc, elementData).then(() => {
          this.closeModal()
          Object.keys(this.form[this.elementType]).forEach(field => {
            if (this.mode === 'add') {
              this.form[this.elementType][field].content = ''
            }
            this.form[this.elementType][field].error = ''
          })
        })

        // Reload Data & Treeview
        if ((this.elementType === 'objective') || this.elementType === 'review' || this.elementType === 'conflict') {
          // this.updateModalContent()
          await this.$store.dispatch('getObjectives', { validFrom: this.$parent.$parent.filterValidFrom, validTo: this.$parent.$parent.filterValidTo, treeView: this.$parent.$parent.objectivesTreeView })
        }
      }
      this.$store.commit('SET_LOADING_STATUS', false)
    },
    updateModalContent () {
      Object.keys(this.form[this.elementType]).forEach(field => {
        let selectedElement = this.selectedObjective
        if (['objective', 'record'].indexOf(this.elementType) === -1) {
          const elementTypePlural = this.elementType + 's'
          selectedElement = this.selectedObjective[elementTypePlural].find(element => {
            return element.id === this.$parent.objectiveModalElementId
          })
          this.$parent.selectedElement = selectedElement
        } else if (this.elementType === 'record') {
          const selectedKeyResult = this.selectedObjective.keyresults.find(keyresult => {
            return keyresult.id === this.$parent.objectiveModalKeyResultId
          })
          selectedElement = selectedKeyResult.records.find(record => {
            return record.id === this.$parent.objectiveModalElementId
          })
          this.$parent.selectedElement = selectedElement
        }
        if (field in selectedElement) {
          if (field === 'responsible') {
            this.form[this.elementType][field].content = selectedElement[field].id
          } else {
            this.form[this.elementType][field].content = selectedElement[field]
          }
          this.form[this.elementType][field].error = ''
        }
      })
      // Kategorie in Number umwandeln
      if (this.elementType === 'objective') {
        this.form[this.elementType].category.content = Number(this.form[this.elementType].category.content)
        // Auswahlfelder aus validFrom/validTo setzen
        this.initializeSelectionsFromDates()
      }
    },
    toggleCriterion (criterion) {
      this.selectedObjective.criterion = criterion
    },
    updateBreakthroughDates () {
      if (this.form.objective.category.content !== 1) return

      this.form.objective.validFrom.content = `${this.breakthroughStartYear}-01-01`
      this.form.objective.validTo.content = `${this.breakthroughEndYear}-12-31`
    },

    initializeSelectionsFromDates () {
      const obj = this.form[this.elementType]

      const from = obj.validFrom.content
      const to = obj.validTo.content

      if (!from || !to) return

      const fromDate = new Date(from)
      const toDate = new Date(to)

      const yearFrom = fromDate.getFullYear()
      const yearTo = toDate.getFullYear()
      const monthFrom = fromDate.getMonth() + 1

      const category = obj.category.content

      // Yearly
      if (category === 0) {
        this.selectedYear = yearFrom
      }

      // Quarterly
      if (category === 2) {
        const quarter = Math.floor((monthFrom - 1) / 3) + 1
        this.selectedQuarter = `${yearFrom}-Q${quarter}`
      }

      // Breakthrough
      if (category === 1) {
        this.breakthroughStartYear = yearFrom
        this.breakthroughEndYear = yearTo
      }
    }

  },

  watch: {
    elementId () {
      this.updateModalContent()
    },
    mode (newMode) {
      if (newMode === 'add') {
        Object.keys(this.form[this.elementType]).forEach(field => {
          if (['status', 'priority'].includes(field)) {
            this.form[this.elementType][field].content = false
          } else if (this.elementType === 'review' && field === 'date') {
            this.form[this.elementType][field].content = new Date().toISOString().substring(0, 10)
          } else if (this.elementType === 'record' && field === 'date') {
            this.form[this.elementType][field].content = new Date().toISOString().substring(0, 10)
          } else {
            this.form[this.elementType][field].content = this.form[this.elementType][field].error = ''
          }
        })
      } else if (newMode === 'update') {
        this.updateModalContent()
      }
    },

    selectedYear (newYear) {
      if (this.form.objective.category.content === 0) {
        this.form.objective.validFrom.content = `${newYear}-01-01`
        this.form.objective.validTo.content = `${newYear}-12-31`
      }
    },

    selectedQuarter (newQuarter) {
      if (this.form.objective.category.content !== 2) return

      const [year, q] = newQuarter.split('-Q')
      const quarter = Number(q)

      const startMonths = { 1: 1, 2: 4, 3: 7, 4: 10 }
      const endMonths = { 1: 3, 2: 6, 3: 9, 4: 12 }

      const startMonth = startMonths[quarter]
      const endMonth = endMonths[quarter]

      // Startdatum: immer der 1.
      this.form.objective.validFrom.content =
        `${year}-${String(startMonth).padStart(2, '0')}-01`

      // Enddatum: letzter Tag des Monats
      const lastDay = new Date(year, endMonth, 0).getDate()

      this.form.objective.validTo.content =
        `${year}-${String(endMonth).padStart(2, '0')}-${lastDay}`
    },

    breakthroughStartYear () {
      this.updateBreakthroughDates()
    },
    breakthroughEndYear () {
      this.updateBreakthroughDates()
    }

  },
  setup () {
    const objectiveEditModal = ref(null)
    const closeModal = () => Modal.getInstance(objectiveEditModal.value)?.hide()
    return { objectiveEditModal, closeModal }
  }
}
</script>
